import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
// @ts-expect-error - lodash.debounce has no type declarations
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import { User } from 'lucide-react';
import { ChipList, DataGrid, FilterAsyncAutoComplete, FilterDateRange, FilterSearch, Spinner } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { PERMISSIONS } from '@repo/shared/domain';
import { ProjectForm } from '@repo/forms';
import { EventEmitter, constructQuery, sortToString, stringToSort, getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getProjects } from 'src/api/admin/projects';
import { getZones } from 'src/api/admin/sites';
import { getPrincipalGroupLabelsSettings } from 'src/api/settings';

interface ProjectsSearch {
	sort?: string;
	page?: number;
	search?: string;
	groupIds?: string;
	zoneIds?: string;
	from?: string;
	until?: string;
	tab?: string;
}

export const Route = createFileRoute('/_admin/projects/')({
	validateSearch: (search: Record<string, unknown>): ProjectsSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		search: search.search as string | undefined,
		groupIds: search.groupIds as string | undefined,
		zoneIds: search.zoneIds as string | undefined,
		from: search.from as string | undefined,
		until: search.until as string | undefined,
		tab: search.tab as string | undefined,
	}),
	component: ProjectsPage,
});

const columns = [
	{
		field: 'name',
		headerName: 'Name',
	},
	{
		field: 'startDate',
		headerName: 'Start Date',
		valueFormatter: (params: { value: string }) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'endDate',
		headerName: 'End Date',
		valueFormatter: (params: { value: string }) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'principalGroupsPopulated',
		headerName: 'Responsible Companies',
		renderCell: (c: { value: Array<{ id: string; name: string }> }) => {
			if (!c.value || !Array.isArray(c.value)) {
				return null;
			}

			// Filter out duplicate groups by id
			const uniqueGroups = c.value.filter((group, index, self) => index === self.findIndex((g) => g.id === group.id));

			return <ChipList data={uniqueGroups} getLabel={(group: { name: string }) => group.name} />;
		},
	},
	{
		field: 'zones',
		headerName: 'Zones',
		flex: 1,
		renderCell: (c: { value: Array<{ name?: string }> }) => (
			<ChipList data={c.value} getLabel={(o: { name?: string }) => o?.name || null} />
		),
		sortable: false,
	},
	{
		field: 'admins',
		headerName: 'Contacts',
		flex: 1,
		renderCell: (c: { value: Array<{ firstName?: string; lastName?: string; email?: string }> }) => (
			<ChipList
				data={c.value.map((user) => ({ ...user, status: undefined }))}
				getLabel={(o: { firstName?: string; lastName?: string; email?: string }) =>
					o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email || null
				}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
	{
		field: 'updatedAt',
		headerName: 'Updated',
		valueFormatter: (params: { value: string }) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
];

const tabs = [
	{
		id: '0',
		label: 'All',
	},
	{
		id: '1',
		label: 'Archived',
	},
];

function ProjectsPage() {
	const searchParams = Route.useSearch();
	const { sort, page, search, groupIds, zoneIds, from, until, tab } = searchParams;

	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { updateValue } = useLocalStorage<string>('projectsBackUrl');
	useBreadcrumbLevel(0, 'Projects');

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const [isFormOpen, setIsFormOpen] = useState(false);

	const activeTab = parseInt(tab || '0', 10);

	const query = constructQuery({
		tab,
		sort: sort || 'name:asc',
		page,
		search,
		groupIds,
		zoneIds,
		from,
		until,
		noPagination: 'false',
	});

	const { data: projectsData, isLoading: projectsLoading } = useQuery({
		queryKey: ['projects', query],
		queryFn: () => getProjects(query),
	});

	const { data: zonesData } = useQuery({
		queryKey: ['zones'],
		queryFn: () => getZones(),
	});

	const { data: principalGroupLabelsSettings } = useQuery({
		queryKey: ['principal-group-labels-settings'],
		queryFn: () => getPrincipalGroupLabelsSettings(),
	});

	const data = useMemo(() => projectsData || { data: [], count: 0, page: 0, pageSize: 25 }, [projectsData]);
	const zones = zonesData?.data || [];
	const principalGroupLabels =
		principalGroupLabelsSettings?.settings?.principalGroupLabels?.map((item: { value: string }) => item.value) || [];

	const defaultProjectData = {
		startDate: startOfDay(new Date()),
		endDate: endOfDay(new Date()),
		adminIds: authenticatedUser ? [authenticatedUser.id] : [],
	};

	const handleCloseForm = () => {
		setIsFormOpen(false);
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpen(true);
	}, []);

	const handleSubmit = async (formData: Record<string, unknown>) => {
		let isError = false;
		let id: string | undefined;

		if (formData) {
			await request
				.post('/api/projects', formData)
				.then(({ body }: { body: { id: string } }) => {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Project saved',
					});
					id = body.id;
				})
				.catch((error: { message?: string; body?: { message?: string } }) => {
					isError = true;
					const errorMessage =
						error?.message || error?.body?.message || 'Failed to save Project. Did you enter all data correctly?';
					EventEmitter.emit('toast', {
						type: 'error',
						message: errorMessage,
					});
				});
		}

		revalidateProjects();

		if (id) {
			updateValue(window.location.href);
			navigate({ to: '/projects/$projectId', params: { projectId: id } });
		}

		if (!isError) {
			setIsFormOpen(false);
		}
	};

	const handleRowClick = ({ row }: { row: { id: string } }) => {
		updateValue(window.location.href);
		navigate({ to: '/projects/$projectId', params: { projectId: row.id } });
	};

	const handleSort = (sortModel: { field: string; sort: 'asc' | 'desc' }[]) => {
		const firstSort = sortModel[0];
		if (!firstSort) {
			return;
		}
		updateQueryParams({ sort: sortToString(firstSort) });
	};

	const handleTabChange = (newTab: string) => {
		updateQueryParams({ tab: newTab });
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const handleFilterChange = (filterData: { groupIds?: string[] | string; search?: string }) => {
		updateQueryParams(filterData);
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const revalidateProjects = () => {
		queryClient.invalidateQueries({ queryKey: ['projects'] });
	};

	const actions = useMemo(() => {
		return verifyPermission(PERMISSIONS.adminGroupsCreate)
			? [
					<Button onClick={handleOpenForm} startIcon={<AddIcon />} variant="outlined" key="add">
						Add Project
					</Button>,
				]
			: [];
	}, [handleOpenForm, verifyPermission]);

	const isLoading = authLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<GenericDataPageHeader
				title="Projects"
				tabs={tabs}
				defaultTab="0"
				onTabChange={handleTabChange}
				actions={actions}
			/>
			<div className="p-4 pt-0">
				{/* Filter */}
				<div className="mt-2 flex flex-row gap-2">
					<div className="w-1/2">
						<FilterSearch
							label="Search Project"
							defaultValue={search || ''}
							onChange={debounce((searchInput: string) => {
								handleSearchChange(searchInput);
							}, 350)}
							sx={{ width: '100%' }}
						/>
					</div>
					<div className="w-1/3">
						<FilterAsyncAutoComplete
							key="groupFilter"
							name="groupIds"
							label="Filter Companies"
							optionLabel="name"
							dataSource="/api/data-sources/groups/data"
							value={groupIds || []}
							onChange={(newGroupIds) => handleFilterChange({ groupIds: newGroupIds })}
							multiple
						/>
					</div>
					{activeTab === 0 && (
						<div className="w-1/6">
							<FilterDateRange
								label="Filter Date Range"
								value={
									from || until
										? {
												from,
												until,
											}
										: undefined
								}
								onChange={handleDateFilterChange}
								sx={{ width: '100%' }}
							/>
						</div>
					)}
				</div>
				<div className="mb-4"></div>
				<Paper variant="outlined">
					<DataGrid
						isLoading={projectsLoading}
						data={data.data}
						rowCount={data.count}
						page={data.page}
						pageSize={data.pageSize}
						columns={columns}
						sortModel={[stringToSort(sort || 'name:asc')]}
						onSortModelChange={handleSort}
						onReload={revalidateProjects}
						onPaginationModelChange={handlePaginationModelChange}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						onRowClick={handleRowClick}
					/>
				</Paper>
			</div>

			{isFormOpen && (
				<ProjectForm
					isOpen={isFormOpen}
					onClose={handleCloseForm}
					onSubmit={handleSubmit}
					data={defaultProjectData}
					principalGroupLabels={principalGroupLabels}
				/>
			)}
		</>
	);
}
